import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService, ACCESS_CHANGED_MESSAGE, SESSION_INVALID_MESSAGE } from "../services/accounts/auth.service";
import { environment } from "../../environment/environment";

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshedToken = new BehaviorSubject<string | null>(null);

  constructor(private auth: AuthService) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // /token/refresh/ is excluded from an Authorization header like the rest of these, but
    // it must NOT self-logout on 401 here: it's always called from within handleAuthError()'s
    // own refresh attempt, which is the single place that decides the right message (session
    // expired vs deliberately invalidated). A second logout() here raced the real one and could
    // clobber sessionStorage with the wrong message — or the wrong one could win the race.
    if (req.url.includes('/token/refresh')) {
      return next.handle(req.clone({ headers: req.headers.delete('Authorization') }));
    }

    const excludedUrls = ['/verify', '/rbac/activate', '/rbac/password-reset'];
    if (excludedUrls.some(url => req.url.includes(url))) {
      const authReq = req.clone({
        headers: req.headers.delete('Authorization')
      });

      return next.handle(authReq).pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.auth.logout(this.messageForAuthError(error));
          }
          return throwError(error);
        }))
    }

    if (req.headers.has('Authorization')) {
      return next.handle(req).pipe(
        catchError((error: any) => this.handleAuthError(req, next, error))
      );
    }

    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${this.auth.getToken()}`
      }
    });
    return next.handle(authReq).pipe(
      catchError((error: any) => this.handleAuthError(req, next, error))
    );
  }

  private handleAuthError(req: HttpRequest<any>, next: HttpHandler, error: any): Observable<HttpEvent<any>> {
    if (error.status !== 401) {
      return throwError(error);
    }

    // The backend deliberately invalidated this session (e.g. an admin changed the
    // user's role, permissions, or module access) — refreshing won't help, so skip
    // straight to a re-login instead of wasting a round-trip on /token/refresh/.
    if (error.error?.code === 'session_invalidated') {
      this.auth.logout(this.messageForAuthError(error));
      return throwError(error);
    }

    if (this.isRefreshing) {
      return this.refreshedToken.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addAuthHeader(req, token as string)))
      );
    }

    this.isRefreshing = true;
    this.refreshedToken.next(null);

    return this.auth.refreshAccessToken().pipe(
      switchMap((data: any) => {
        this.isRefreshing = false;
        this.refreshedToken.next(data.access);
        return next.handle(this.addAuthHeader(req, data.access));
      }),
      catchError((refreshError: any) => {
        this.isRefreshing = false;
        this.auth.logout(this.messageForAuthError(refreshError));
        return throwError(refreshError);
      })
    );
  }

  /** Distinguishes the two 401 shapes: a deliberate, backend-initiated session kill
   *  (code: "session_invalidated") vs a plain bad/expired token (e.g. "token_not_valid"). */
  private messageForAuthError(error: any): string {
    return error?.error?.code === 'session_invalidated' ? ACCESS_CHANGED_MESSAGE : SESSION_INVALID_MESSAGE;
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
